import React, { useEffect, useState } from "react";
import "./permission.css";

/**
 * PermissionsChecker Component
 *
 * Checks and manages user camera and microphone permissions.
 * Shows success or warning alerts and provides a help modal
 * with browser-specific instructions for enabling permissions.
 *
 * @return {React.ReactNode} A UI section for checking and guiding users on camera/mic access.
 */

const PermissionsChecker = () => {
  const [hasPermission, setHasPermission] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isSafari, setIsSafari] = useState(false);

  const requestPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      stream.getTracks().forEach((track) => track.stop());
      setHasPermission(true);
      setErrorMessage("");
    } catch (err) {
      console.error("Permission error:", err);
      setHasPermission(false);
      if (err.name === "NotAllowedError") {
        setErrorMessage(
          "Camera or microphone access has been blocked. Please follow the steps shown in our demo to enable permissions."
        );
      } else {
        setErrorMessage("Unable to access camera or microphone.");
      }
    }
  };

  const closeModal = () => setShowModal(false);
  const openModal = () => setShowModal(true);
  const toggleBrowser = () => setIsSafari((prev) => !prev);

  useEffect(() => {
    requestPermissions();
  }, []);

  return (
    <>
      <div className="permission-container mt-4">
        {hasPermission === true && (
          <div
            className="alert alert-success d-flex align-items-center"
            role="alert"
          >
            <svg
              className="bi flex-shrink-0 me-3"
              width="24"
              height="24"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
            </svg>
            <div>
              <strong>Success!</strong> Camera and microphone access granted.
            </div>
          </div>
        )}

        {hasPermission === false && (
          <div
            className="alert alert-warning d-flex align-items-center"
            role="alert"
          >
            <svg
              className="bi flex-shrink-0 me-3"
              width="24"
              height="24"
              fill="red"
              viewBox="0 0 16 16"
            >
              <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
            </svg>
            <div className="flex-grow-1">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>Permission Required!</strong>
                  <div className="mt-2">{errorMessage}</div>
                </div>
                <div className="ms-3">
                  <button
                    onClick={openModal}
                    className="btn btn-warning btn-sm"
                  >
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      className="me-1"
                    >
                      <path d="M14.778.085A.5.5 0 0 1 15 .5V8a.5.5 0 0 1-.314.464L8.5 10.385V15.5a.5.5 0 0 1-.314.464l-7-2.5A.5.5 0 0 1 1 13V6.5a.5.5 0 0 1 .314-.464L8.5 3.615V.5a.5.5 0 0 1 .778-.415l5.5 2.5zM2 7.04v5.347l6 2.143V9.653L2 7.04zM9 8.347v4.877l5-1.786V3.653L9 8.347z" />
                    </svg>
                    Watch Video meeting
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      
      {showModal && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{
              zIndex: 1055,
              backgroundColor: "rgba(0, 0, 0, 0.5)", 
            }}
            onClick={closeModal}
          >
            <div
              className="modal-dialog modal-lg modal-dialog-centered"
              role="document"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header bg-primary text-white d-flex justify-content-between align-items-center">
                  <h5 className="modal-title d-flex align-items-center">
                    <svg
                      width="24"
                      height="24"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      className="me-2"
                    >
                      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4ZM2 3a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z" />
                    </svg>
                    How to Enable Camera & Microphone Access
                  </h5>
                  
                  <div className="browser-switch d-flex align-items-center">
                    <span
                      className={`me-2 small browser-label ${
                        !isSafari ? "active-browser" : ""
                      }`}
                    >
                      Chrome / Firefox / Edge
                    </span>

                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="browserSwitch"
                        checked={isSafari}
                        onChange={toggleBrowser}
                      />
                    </div>

                    <span
                      className={`ms-2 small browser-label ${
                        isSafari ? "active-browser" : ""
                      }`}
                    >
                      Safari
                    </span>
                  </div>

                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    onClick={closeModal}
                    aria-label="Close"
                  ></button>
                </div>

                <div className="modal-body p-4">
                  <div className="text-center mb-4">
                    <div className="alert alert-info">
                      <strong>📹 Quick Guide:</strong>{" "}
                      Follow these simple steps to enable your camera and
                      microphone permissions in{" "}
                      <strong>
                        {isSafari ? "Safari" : "Chrome / Firefox / Edge"}
                      </strong>
                    </div>
                  </div>

                  
                  <div className="text-center mb-4">
                    <div
                      className="border rounded p-3 bg-light"
                      style={{
                        minHeight: "300px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={
                          isSafari
                            ? "/Safari-permissions.gif"
                            : "/Chrome-permissions.gif"
                        }
                        alt={
                          isSafari
                            ? "Safari permissions setup"
                            : "Chrome/Firefox/Edge permissions setup"
                        }
                        className="img-fluid rounded shadow-sm"
                        style={{ maxHeight: "380px" }}
                      />
                    </div>
                  </div>

                  
                  {!isSafari ? (
                    <div className="row">
                      <div className="col-md-6">
                        <h6 className="text-primary">
                          Method 1: Using Address Bar
                        </h6>
                        <ol className="small">
                          <li>
                            Look for the{" "}
                            <img
                              src="/settings.png"
                              style={{ maxHeight: "15px" }}
                              alt=""
                            />{" "}
                            or 🎥 icon in your address bar
                          </li>
                          <li>Click on the icon</li>
                          <li>Select "Site Settings"</li>
                          <li>
                            Change Camera and Microphone to <b>"Allow"</b>
                          </li>
                          <li>Refresh the page</li>
                        </ol>
                      </div>
                      <div className="col-md-6">
                        <h6 className="text-primary">
                          Method 2: Using Browser Settings
                        </h6>
                        <ol className="small">
                          <li>Click the three dots (⋮) in your browser</li>
                          <li>Go to Settings → Privacy & Security</li>
                          <li>Click "Site Settings"</li>
                          <li>Select "Camera" and "Microphone"</li>
                          <li>Allow sites to ask for permissions</li>
                        </ol>
                      </div>
                    </div>
                  ) : (
                    <div>
                        <h6 className="text-primary">
                            Steps for Safari (macOS / iOS)
                        </h6>
                        <ol className="small">
                            <li>
                            Open Safari and navigate to the website you want to allow camera and microphone access for.
                            </li>
                            <li>
                            In the Safari top menu, click <b>Safari</b> → <b>Settings for This Website...</b>
                            </li>
                            <li>
                            A pop-up will appear with options for <b>Camera</b> and <b>Microphone</b>. 
                            <ul>
                                <li>Select <b>Allow</b> for both Camera and Microphone.</li>
                            </ul>
                            </li>
                            <li>
                            Close the settings pop-up and refresh the website to apply the changes.
                            </li>
                            <li>
                            If the website is still not able to access your camera or microphone:
                            <ul>
                                <li>Go to <b>Safari</b> → <b>Preferences</b> → <b>Websites</b> tab</li>
                                <li>Select <b>Camera</b> from the left menu and make sure the website is set to <b>Allow</b></li>
                                <li>Select <b>Microphone</b> from the left menu and set it to <b>Allow</b></li>
                            </ul>
                            </li>
                            <li>
                            Refresh the page again. The site should now have access to your camera and microphone.
                            </li>
                        </ol>
                        </div>
                     )}
                  <div className="alert alert-warning mt-3">
                    <small>
                      <strong>💡 Tip:</strong> Make sure your browser is up to
                      date and that no other applications are using your camera
                      or microphone.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PermissionsChecker;

